import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { Link } from "react-router-dom";

import {
  resendVerificationInputSchema,
  verifyEmailInputSchema,
  type ResendVerificationInput,
  type SignupAcceptedResponse,
  type VerifyEmailResponse,
} from "@learn-flow/contracts";

import { Logo } from "../../components/logo.js";
import { ApiError, apiRequest } from "../../lib/api.js";

type PasswordForm = { password: string };
type VerificationSubmission = PasswordForm & { token: string };
let capturedToken: string | null | undefined;
const VERIFIED_SESSION_KEY = "learnflow-email-verification-complete";

export function VerifyEmailPage(): React.JSX.Element {
  const [token, setToken] = useState(captureTokenFromFragment);
  const tokenRef = useRef(token);
  const [verifiedFromSession, setVerifiedFromSession] = useState(readVerifiedSession);
  const [verifiedThisView, setVerifiedThisView] = useState(false);
  const [verificationFailed, setVerificationFailed] = useState(false);
  const {
    register,
    handleSubmit,
    clearErrors,
    reset,
    setError,
    formState: { errors },
  } = useForm<PasswordForm>({
    resolver: zodResolver(verifyEmailInputSchema.pick({ password: true })),
    defaultValues: { password: "" },
  });
  const verification = useMutation({
    mutationFn: ({ password, token: submittedToken }: VerificationSubmission) =>
      apiRequest<VerifyEmailResponse>("/api/v1/auth/verify-email", {
        method: "POST",
        body: JSON.stringify({ token: submittedToken, password }),
      }),
    onSuccess: (_response, submission) => {
      if (tokenRef.current !== submission.token) return;
      capturedToken = null;
      tokenRef.current = null;
      setToken(null);
      rememberVerifiedSession();
      window.history.replaceState(null, "", "/verify-email");
      setVerificationFailed(false);
      setVerifiedThisView(true);
    },
    onError: (_error, submission) => {
      if (tokenRef.current !== submission.token) return;
      setVerificationFailed(true);
      setError("root", {
        message: "This link or password could not be verified. Request a new link and try again.",
      });
    },
  });

  useEffect(() => {
    document.title = "Verify email · MemoKite";
  }, []);

  useEffect(() => {
    const captureNewFragment = () => {
      const next = takeTokenFromFragment();
      if (!next.found) return;
      tokenRef.current = next.token;
      setToken(next.token);
      setVerifiedFromSession(false);
      setVerifiedThisView(false);
      setVerificationFailed(false);
      verification.reset();
      clearErrors();
      reset({ password: "" });
    };
    window.addEventListener("hashchange", captureNewFragment);
    return () => {
      window.removeEventListener("hashchange", captureNewFragment);
    };
  }, [clearErrors, reset, verification]);

  const verified = verifiedFromSession || verifiedThisView;
  return (
    <main className="auth-simple-shell">
      <section className="auth-simple-card" aria-labelledby="verify-heading">
        <Logo />
        {verified ? (
          <>
            <span className="section-kicker">Email verified</span>
            <h1 id="verify-heading">Your account is ready</h1>
            <p>Your email has been verified. Log in with the password you used to sign up.</p>
            <Link className="primary-button auth-link-button" to="/login">
              Go to login
            </Link>
          </>
        ) : (
          <>
            <span className="section-kicker">One secure step</span>
            <h1 id="verify-heading">Verify your email</h1>
            {!token ? (
              <>
                <p>This verification link is missing or invalid.</p>
                <ResendVerificationForm />
              </>
            ) : (
              <>
                <p>Enter the password you used to sign up to finish verifying your account.</p>
                <form
                  className="auth-email-form"
                  onSubmit={handleSubmit((input) => {
                    if (token && !verification.isPending) {
                      verification.mutate({ ...input, token });
                    }
                  })}
                  noValidate
                >
                  <label htmlFor="verification-password">
                    Signup password
                    <input
                      id="verification-password"
                      type="password"
                      autoComplete="current-password"
                      disabled={verification.isPending}
                      aria-invalid={errors.password || errors.root ? "true" : undefined}
                      {...register("password")}
                    />
                  </label>
                  {errors.password?.message && (
                    <p className="field-error">{errors.password.message}</p>
                  )}
                  <div className="auth-message" aria-live="assertive" aria-atomic="true">
                    {errors.root?.message && (
                      <p className="login-error" role="alert">
                        {errors.root.message}
                      </p>
                    )}
                  </div>
                  <button
                    type="submit"
                    className="primary-button"
                    disabled={verification.isPending}
                  >
                    {verification.isPending ? "Verifying…" : "Verify email"}
                  </button>
                </form>
                {verificationFailed ? <ResendVerificationForm /> : null}
              </>
            )}
          </>
        )}
      </section>
    </main>
  );
}

function readVerifiedSession(): boolean {
  try {
    return window.sessionStorage.getItem(VERIFIED_SESSION_KEY) === "true";
  } catch {
    return false;
  }
}

function rememberVerifiedSession(): void {
  try {
    window.sessionStorage.setItem(VERIFIED_SESSION_KEY, "true");
  } catch {
    // Verification is durable on the server even if browser storage is unavailable.
  }
}

function forgetVerifiedSession(): void {
  try {
    window.sessionStorage.removeItem(VERIFIED_SESSION_KEY);
  } catch {
    // A new fragment still resets in-memory success state if browser storage is unavailable.
  }
}

export function ResendVerificationForm({
  initialEmail = "",
}: {
  initialEmail?: string;
}): React.JSX.Element {
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<ResendVerificationInput>({
    resolver: zodResolver(resendVerificationInputSchema),
    defaultValues: { email: initialEmail },
  });
  const resend = useMutation({
    mutationFn: (input: ResendVerificationInput) =>
      apiRequest<SignupAcceptedResponse>("/api/v1/auth/verification/resend", {
        method: "POST",
        body: JSON.stringify(input),
      }),
    onError: (error: Error) => {
      setError("root", {
        message:
          error instanceof ApiError && error.status === 429
            ? "Too many requests. Please try again later."
            : "We couldn’t request another link. Please try again.",
      });
    },
  });
  return (
    <form
      className="auth-email-form auth-resend-form"
      onSubmit={handleSubmit((input) => {
        if (!resend.isPending) resend.mutate(input);
      })}
      noValidate
    >
      <h2>Request a new link</h2>
      <label htmlFor="resend-email">
        Email
        <input
          id="resend-email"
          type="email"
          inputMode="email"
          autoComplete="email"
          autoCapitalize="none"
          spellCheck={false}
          disabled={resend.isPending || resend.isSuccess}
          aria-invalid={errors.email ? "true" : undefined}
          {...register("email")}
        />
      </label>
      {errors.email?.message && <p className="field-error">{errors.email.message}</p>}
      {errors.root?.message && (
        <p className="login-error" role="alert">
          {errors.root.message}
        </p>
      )}
      {resend.isSuccess ? (
        <p role="status">{resend.data.message}</p>
      ) : (
        <button className="soft-button" type="submit" disabled={resend.isPending}>
          {resend.isPending ? "Requesting…" : "Send a new link"}
        </button>
      )}
    </form>
  );
}

function captureTokenFromFragment(): string | null {
  const next = takeTokenFromFragment();
  return next.found ? next.token : (capturedToken ?? null);
}

function takeTokenFromFragment(): { found: true; token: string } | { found: false; token: null } {
  const tokenInFragment = new URLSearchParams(window.location.hash.slice(1)).get("token");
  if (tokenInFragment === null) return { found: false, token: null };
  capturedToken = tokenInFragment;
  forgetVerifiedSession();
  window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}`);
  return { found: true, token: tokenInFragment };
}
